import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetNotificationsQuery } from '@/entities/notification'
import { CounterBadge } from '@/shared/ui/CounterBadge'
import { Icon } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/IconButton'
import { Popover } from '@/shared/ui/Popover'
import { NotificationPanel, type NotificationLinkSlotProps } from './NotificationPanel'

/**
 * Лёгкая выборка для шапки: число непрочитанных — из `summary.unread`, лента — первая короткая
 * страница. Всё остальное на странице `/notifications`.
 */
const BELL_PAGE_SIZE = 6

export type NotificationBellProps = {
  /** Ссылку «Все уведомления» рисует потребитель: каркас о роутере не знает (как `renderLogo`). */
  renderViewAllLink: (props: NotificationLinkSlotProps) => React.ReactNode
}

/**
 * Колокольчик в шапке: кнопка со счётчиком непрочитанных и выпадающая лента последних событий.
 * Счётчик берётся из ответа сервера (`summary.unread`) — на клиенте не считается. Фокус после
 * закрытия панели остаётся на кнопке (`Popover` возвращает его на якорь).
 */
export function NotificationBell({ renderViewAllLink }: NotificationBellProps) {
  const { t } = useTranslation('notifications')
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const { data, isLoading, isError, refetch } = useGetNotificationsQuery({
    page: 1,
    pageSize: BELL_PAGE_SIZE,
  })
  const unread = data?.summary.unread ?? 0

  return (
    <>
      <IconButton
        ref={anchorRef}
        icon={<Icon name="bell" size="md" />}
        label={unread > 0 ? t('bell.labelCount', { count: unread }) : t('bell.label')}
        variant="secondary"
        size="md"
        expanded={open}
        aria-haspopup="dialog"
        badge={
          unread > 0 ? (
            <CounterBadge value={unread} tone="danger" size="xs" decorative />
          ) : undefined
        }
        onClick={() => setOpen((value) => !value)}
      />

      <Popover anchorRef={anchorRef} open={open} onOpenChange={setOpen} placement="bottom-end">
        <NotificationPanel
          items={data?.items ?? []}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          renderViewAllLink={renderViewAllLink}
          onNavigate={() => setOpen(false)}
        />
      </Popover>
    </>
  )
}
